import React, { useState, useEffect, useContext } from "react";
import {
  FaPlus,
  FaTrash,
  FaTimes,
  FaSave,
  FaQuestionCircle,
  FaKeyboard,
  FaFileUpload,
  FaLink,
} from "react-icons/fa";
import { HashLoader } from "react-spinners";
import {
  Prompt,
  PromptFormData,
  PromptInput,
  PromptOutput,
  ValidationState,
} from "../types";
import { useAuthenticatedFetch } from "../../../../../Hooks/useFetch";
import config from "../../../../../config";
import { toast } from "react-hot-toast";
import { GoogleAnalyticsContext } from "../../../../../Hooks/GoogleAnalyticsContext";
import HowToGuide from "./HowToGuide";
import { TEMPLATES } from "./templates";

interface PromptEditorProps {
  selectedPrompt?: Prompt | null;
  onCancel: () => void;
  onSave: () => void;
}

const PromptEditor: React.FC<PromptEditorProps> = ({
  selectedPrompt,
  onCancel,
  onSave,
}) => {
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const [formData, setFormData] = useState<PromptFormData>({
    title: selectedPrompt?.title || "My custom tool",
    promptText:
      selectedPrompt?.promptText ||
      `You are an AI assistant. Please help me with the following task:

{{text}}

Please provide a clear and detailed response.`,
    inputs: selectedPrompt?.inputs.map((input: PromptInput) => ({
      ...input,
      renderPreferences:
        typeof input.renderPreferences === "string"
          ? JSON.parse(input.renderPreferences)
          : input.renderPreferences || {
              renderType: input.type,
              allowedSources: ["direct"],
            },
    })) || [
      {
        name: "text",
        description: "The main input text",
        type: "textarea",
        required: true,
        renderPreferences: {
          renderType: "textarea",
          allowedSources: ["direct"],
        },
      },
    ],
    outputs: selectedPrompt?.outputs || [
      { name: "output", description: "The generated response", type: "string" },
    ],
  });

  const [validationState, setValidationState] = useState<ValidationState>({
    errors: {},
    missingVariables: [],
    unusedInputs: [],
  });

  const [isGuideOpen, setIsGuideOpen] = useState(!selectedPrompt);
  const [activeTab, setActiveTab] = useState("basic"); // basic, inputs, outputs, advanced

  const { callAPI: createPrompt, isLoading: isCreating } =
    useAuthenticatedFetch({
      url: `${config.API_URI}/api/prompt`,
      method: "POST",
    });

  const { callAPI: updatePrompt, isLoading: isUpdating } =
    useAuthenticatedFetch({
      url: selectedPrompt
        ? `${config.API_URI}/api/prompt/${selectedPrompt.id}`
        : "",
      method: "PUT",
    });

  // Update useEffect to only validate when there's content
  useEffect(() => {
    if (formData.title.trim() || formData.promptText.trim()) {
      validatePrompt();
    } else {
      // Reset validation state when there's no content
      setValidationState({
        errors: {},
        missingVariables: [],
        unusedInputs: [],
      });
    }
  }, [formData.title, formData.promptText, formData.inputs]);

  // Add useEffect to handle URL source mappings
  useEffect(() => {
    const updatedInputs = formData.inputs.map((input) => {
      const renderPrefs = input.renderPreferences || {
        renderType: input.type,
        allowedSources: ["direct"],
      };

      // Create a copy to modify
      const updated = { ...input };

      // Ensure sourceMappings exists
      if (!updated.sourceMappings) {
        updated.sourceMappings = [];
      }

      // Handle URL source mapping
      if (renderPrefs.allowedSources?.includes("url")) {
        // Check if URL mapping already exists
        const hasUrlMapping = updated.sourceMappings.some(
          (mapping) =>
            mapping.sourceField === "url" &&
            mapping.processorName === "urlProcessor"
        );

        if (!hasUrlMapping) {
          updated.sourceMappings.push({
            sourceField: "url",
            processorName: "urlProcessor",
          });
        }
      }

      // Handle document source mapping
      if (renderPrefs.allowedSources?.includes("document")) {
        // Check if document mapping already exists
        const hasDocMapping = updated.sourceMappings.some(
          (mapping) =>
            mapping.sourceField === "document" &&
            mapping.processorName === "documentProcessor"
        );

        if (!hasDocMapping) {
          updated.sourceMappings.push({
            sourceField: "document",
            processorName: "documentProcessor",
          });
        }
      }

      return updated;
    });

    // Only update if changes were made
    if (JSON.stringify(updatedInputs) !== JSON.stringify(formData.inputs)) {
      setFormData((prev) => ({
        ...prev,
        inputs: updatedInputs,
      }));
    }
  }, [formData.inputs]);

  const validatePrompt = () => {
    const errors: Record<string, string> = {};
    const missing: string[] = [];
    const unused: string[] = [];

    if (!formData.title.trim()) {
      errors.title = "Tool name is required";
    }

    if (!formData.promptText.trim()) {
      errors.promptText = "Instructions are required";
    }

    formData.inputs.forEach((input: PromptInput, index: number) => {
      if (!input.name.trim()) {
        errors[`input_${index}`] = "Name is required";
      }
    });

    formData.outputs.forEach((output: PromptOutput, index: number) => {
      if (!output.name.trim()) {
        errors[`output_${index}`] = "Name is required";
      }
    });

    const variableRegex = /\{\{([^}]+)\}\}/g;
    const usedVariables = new Set<string>();
    let match;

    while ((match = variableRegex.exec(formData.promptText)) !== null) {
      usedVariables.add(match[1].trim());
    }

    usedVariables.forEach((variable: string) => {
      if (
        !formData.inputs.some((input: PromptInput) => input.name === variable)
      ) {
        missing.push(variable);
      }
    });

    formData.inputs.forEach((input: PromptInput) => {
      if (!usedVariables.has(input.name) && input.name.trim()) {
        unused.push(input.name);
      }
    });

    setValidationState({
      errors,
      missingVariables: missing,
      unusedInputs: unused,
    });

    return Object.keys(errors).length === 0 && missing.length === 0;
  };

  const handleSave = async () => {
    if (!validatePrompt()) {
      logEvent(
        "CustomToolAction",
        "SaveTool",
        `tool_${selectedPrompt?.id || "new"}_validation_failed`
      );
      toast.error("Please fix the errors before saving");
      return;
    }

    // Process inputs to ensure correct data types and include renderPreferences
    const processedInputs = formData.inputs.map((input) => {
      const processedInput = { ...input };

      // Set dataType based on input type
      if (input.type === "number") {
        processedInput.dataType = "number";
      } else {
        processedInput.dataType = "string";
      }

      // Ensure renderPreferences is included
      if (!processedInput.renderPreferences) {
        processedInput.renderPreferences = {
          renderType: input.type,
          allowedSources: ["direct"],
        };
      }

      return processedInput;
    });

    const promptData = {
      title: formData.title,
      promptText: formData.promptText,
      inputs: processedInputs,
      outputs: formData.outputs,
    };

    try {
      if (selectedPrompt) {
        await updatePrompt(JSON.stringify(promptData));
        logEvent(
          "CustomToolAction",
          "SaveTool",
          `tool_${selectedPrompt.id}_update_success`
        );
      } else {
        await createPrompt(JSON.stringify(promptData));
        logEvent("CustomToolAction", "SaveTool", "new_tool_creation_success");
      }
      toast.success(
        selectedPrompt
          ? "Custom tool updated successfully"
          : "Custom tool created successfully"
      );
      onSave();
    } catch (error) {
      logEvent(
        "CustomToolAction",
        "SaveTool",
        `tool_${selectedPrompt?.id || "new"}_error`
      );
      toast.error("Failed to save custom tool");
    }
  };

  const addInput = () => {
    setFormData({
      ...formData,
      inputs: [
        ...formData.inputs,
        {
          name: "",
          description: "",
          type: "text",
          required: false,
          renderPreferences: {
            renderType: "text",
            allowedSources: ["direct"],
          },
        },
      ],
    });
  };

  const removeInput = (index: number) => {
    setFormData({
      ...formData,
      inputs: formData.inputs.filter((_, i: number) => i !== index),
    });
  };

  const updateInput = (index: number, field: keyof PromptInput, value: any) => {
    const updatedInputs = [...formData.inputs];
    updatedInputs[index] = { ...updatedInputs[index], [field]: value };
    setFormData({ ...formData, inputs: updatedInputs });
  };

  const addOutput = () => {
    setFormData({
      ...formData,
      outputs: [
        ...formData.outputs,
        { name: "", description: "", type: "string" },
      ],
    });
  };

  const removeOutput = (index: number) => {
    setFormData({
      ...formData,
      outputs: formData.outputs.filter((_, i: number) => i !== index),
    });
  };

  const updateOutput = (
    index: number,
    field: keyof PromptOutput,
    value: any
  ) => {
    const updatedOutputs = [...formData.outputs];
    updatedOutputs[index] = { ...updatedOutputs[index], [field]: value };
    setFormData({ ...formData, outputs: updatedOutputs });
  };

  const loadExampleTemplate = (template: string) => {
    logEvent("CustomToolAction", "LoadTemplate", template);
    if (template === "summarizer") {
      setFormData({
        title: TEMPLATES.SUMMARIZER.title,
        promptText: TEMPLATES.SUMMARIZER.promptText,
        inputs: TEMPLATES.SUMMARIZER.inputs,
        outputs: TEMPLATES.SUMMARIZER.outputs,
      });
    } else if (template === "quiz") {
      setFormData({
        title: TEMPLATES.QUIZ.title,
        promptText: TEMPLATES.QUIZ.promptText,
        inputs: TEMPLATES.QUIZ.inputs,
        outputs: TEMPLATES.QUIZ.outputs,
      });
    }
    setTimeout(() => validatePrompt(), 100);
  };

  // Update allowed sources for an input
  const updateAllowedSources = (
    inputIndex: number,
    source: string,
    enabled: boolean
  ) => {
    const updatedInputs = [...formData.inputs];
    const input = updatedInputs[inputIndex];

    if (!input.renderPreferences) {
      input.renderPreferences = {
        renderType: input.type,
        allowedSources: ["direct"],
      };
    }

    let sources = [...(input.renderPreferences.allowedSources || ["direct"])];

    if (enabled) {
      if (!sources.includes(source)) {
        sources.push(source);
      }
    } else {
      sources = sources.filter((s) => s !== source);
      // Always keep at least direct source
      if (sources.length === 0) {
        sources = ["direct"];
      }
    }

    input.renderPreferences.allowedSources = sources;

    setFormData({ ...formData, inputs: updatedInputs });
  };

  // Add a new option to a select input
  const addOption = (inputIndex: number) => {
    const updatedInputs = [...formData.inputs];
    const input = updatedInputs[inputIndex];

    if (!input.renderPreferences) {
      input.renderPreferences = {
        renderType: "select",
        allowedSources: ["direct"],
        options: [],
      };
    }

    if (!input.renderPreferences.options) {
      input.renderPreferences.options = [];
    }

    input.renderPreferences.options.push({ label: "", value: "" });
    setFormData({ ...formData, inputs: updatedInputs });
  };

  // Update an option for a select input
  const updateInputOptions = (
    inputIndex: number,
    optionIndex: number,
    field: "label" | "value",
    value: string | number
  ) => {
    const updatedInputs = [...formData.inputs];
    const input = updatedInputs[inputIndex];

    if (!input.renderPreferences) {
      input.renderPreferences = {
        renderType: "select",
        allowedSources: ["direct"],
        options: [],
      };
    }

    if (!input.renderPreferences.options) {
      input.renderPreferences.options = [];
    }

    // Ensure the option at the specified index exists
    if (!input.renderPreferences.options[optionIndex]) {
      input.renderPreferences.options[optionIndex] = { label: "", value: "" };
    }

    const options = input.renderPreferences.options;
    if (field === "label") {
      options[optionIndex].label = String(value);
    } else {
      options[optionIndex].value = value;
    }

    setFormData({ ...formData, inputs: updatedInputs });
  };

  // Remove an option from a select input
  const removeOption = (inputIndex: number, optionIndex: number) => {
    const updatedInputs = [...formData.inputs];
    const input = updatedInputs[inputIndex];

    if (input.renderPreferences?.options) {
      input.renderPreferences.options = input.renderPreferences.options.filter(
        (_, i: number) => i !== optionIndex
      );

      setFormData({ ...formData, inputs: updatedInputs });
    }
  };

  return (
    <>
      <HowToGuide isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {selectedPrompt
              ? "Edit Your Custom Tool"
              : "Create a New Custom Tool"}
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsGuideOpen(true)}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <FaQuestionCircle className="mr-1" />
              <span className="text-sm">Help Guide</span>
            </button>
            <button
              onClick={onCancel}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {!selectedPrompt && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
            <h3 className="font-medium text-blue-800 mb-2">
              Quick Start Templates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => loadExampleTemplate("summarizer")}
                className="p-4 border border-blue-200 rounded-md hover:bg-blue-100 text-left transition-colors"
              >
                <div className="font-medium text-blue-800 mb-1">
                  Content Summarizer
                </div>
                <div className="text-sm text-blue-600">
                  Create easy-to-read summaries of any text with customizable
                  length and focus
                </div>
              </button>

              <button
                type="button"
                onClick={() => loadExampleTemplate("quiz")}
                className="p-4 border border-blue-200 rounded-md hover:bg-blue-100 text-left transition-colors"
              >
                <div className="font-medium text-blue-800 mb-1">
                  Quiz Generator
                </div>
                <div className="text-sm text-blue-600">
                  Create customized quizzes from any content
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Tab navigation */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            className={`py-2 px-4 font-medium ${
              activeTab === "basic"
                ? "text-yellow-500 border-b-2 border-yellow-500"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("basic")}
          >
            Basics
          </button>
          <button
            className={`py-2 px-4 font-medium ${
              activeTab === "inputs"
                ? "text-yellow-500 border-b-2 border-yellow-500"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("inputs")}
          >
            Variables
          </button>
          <button
            className={`py-2 px-4 font-medium ${
              activeTab === "outputs"
                ? "text-yellow-500 border-b-2 border-yellow-500"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("outputs")}
          >
            Results
          </button>
        </div>

        {/* Basic Info Tab */}
        {activeTab === "basic" && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tool Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className={`w-full p-2 border ${
                  validationState.errors.title
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                placeholder="Give your tool a descriptive name"
              />
              {validationState.errors.title && (
                <p className="mt-1 text-sm text-red-600">
                  {validationState.errors.title}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instructions for AI <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center mb-2">
                <p className="text-sm text-gray-500 flex-grow">
                  Use <code className="bg-gray-100 px-1">{"{{name}}"}</code> to
                  indicate information you'll provide
                </p>
              </div>
              <textarea
                value={formData.promptText}
                onChange={(e) =>
                  setFormData({ ...formData, promptText: e.target.value })
                }
                rows={8}
                className={`w-full p-2 border ${
                  validationState.errors.promptText ||
                  validationState.missingVariables.length > 0
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                placeholder="Write clear instructions for what you want the AI to do."
              />
              {validationState.errors.promptText && (
                <p className="mt-1 text-sm text-red-600">
                  {validationState.errors.promptText}
                </p>
              )}

              {/* Variable validation feedback */}
              {validationState.missingVariables.length > 0 && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700 font-medium">
                    Information needed but not defined:
                  </p>
                  <ul className="list-disc pl-5 text-sm text-red-600 mt-1">
                    {validationState.missingVariables.map((variable, i) => (
                      <li key={i}>
                        <code>{`{{${variable}}}`}</code>
                        <button
                          onClick={() => {
                            const newInput = {
                              name: variable,
                              description: `Information about ${variable}`,
                              type: "text",
                              required: true,
                              renderPreferences: {
                                renderType: "text",
                                allowedSources: ["direct"],
                              },
                            };
                            setFormData({
                              ...formData,
                              inputs: [...formData.inputs, newInput],
                            });
                            setActiveTab("inputs");
                          }}
                          className="ml-2 text-sm text-blue-600 hover:text-blue-800"
                        >
                          Add
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {validationState.unusedInputs.length > 0 && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-700 font-medium">
                    Information defined but not used:
                  </p>
                  <ul className="list-disc pl-5 text-sm text-yellow-600 mt-1">
                    {validationState.unusedInputs.map((input, i) => (
                      <li key={i}>
                        <code>{input}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <h4 className="font-medium text-yellow-800 mb-2">
                Writing Effective Instructions
              </h4>
              <p className="text-sm text-yellow-700 mb-2">
                Good instructions are clear, specific, and tell the AI exactly
                what you want it to do.
              </p>
              <div className="bg-white p-3 rounded border border-yellow-200 text-sm">
                <p className="font-medium text-gray-700 mb-1">Example:</p>
                <p className="text-gray-600">
                  "Create a summary of the {"{{text}}"} that focuses on{" "}
                  {"{{focus_area}}"}. The summary should be {"{{length}}"}{" "}
                  paragraphs long and written at a {"{{reading_level}}"} level."
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Inputs Tab */}
        {activeTab === "inputs" && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <div>
                <h3 className="text-lg font-medium text-gray-700">Variables</h3>
                <p className="text-sm text-gray-500">
                  Define what variables users need to provide when using your
                  tool
                </p>
              </div>
              <button
                onClick={addInput}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
              >
                <FaPlus className="inline mr-1" /> Add Variable
              </button>
            </div>

            {formData.inputs.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-500">
                  No variables defined yet. Add some using the button above.
                </p>
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                {formData.inputs.map((input, index) => (
                  <div
                    key={index}
                    className={`p-4 border ${
                      validationState.errors[`input_${index}`]
                        ? "border-red-500 bg-red-50"
                        : validationState.unusedInputs.includes(input.name)
                        ? "border-yellow-200 bg-yellow-50"
                        : "border-gray-200"
                    } rounded-md`}
                  >
                    <div className="flex justify-between mb-4">
                      <span className="text-sm font-medium">
                        Variable #{index + 1}
                      </span>
                      <div className="flex items-center">
                        {validationState.unusedInputs.includes(input.name) &&
                          input.name && (
                            <span className="text-sm text-yellow-600 mr-3">
                              Not used in instructions
                            </span>
                          )}
                        {index > 0 && (
                          <button
                            onClick={() => removeInput(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTrash size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Name <span className="text-red-500">*</span>
                          <span className="ml-1 text-xs text-gray-500">
                            (used in {"{{name}}"} format)
                          </span>
                        </label>
                        <input
                          type="text"
                          value={input.name}
                          onChange={(e) =>
                            updateInput(index, "name", e.target.value)
                          }
                          className={`w-full p-2 border ${
                            validationState.errors[`input_${index}`]
                              ? "border-red-500 bg-red-50"
                              : "border-gray-300"
                          } rounded-md text-sm`}
                          placeholder="e.g., text, length, level"
                        />
                        {validationState.errors[`input_${index}`] && (
                          <p className="mt-1 text-sm text-red-600">
                            {validationState.errors[`input_${index}`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Type
                        </label>
                        <select
                          value={input.type}
                          onChange={(e) => {
                            const newType = e.target.value;
                            const updatedInputs = [...formData.inputs];
                            const currentInput = updatedInputs[index];

                            // Update the type
                            currentInput.type = newType;

                            // Initialize or update renderPreferences
                            if (!currentInput.renderPreferences) {
                              currentInput.renderPreferences = {
                                renderType: newType,
                                allowedSources: ["direct"],
                                options:
                                  newType === "select"
                                    ? [{ label: "", value: "" }]
                                    : undefined,
                              };
                            } else {
                              currentInput.renderPreferences = {
                                ...currentInput.renderPreferences,
                                renderType: newType,
                                options:
                                  newType === "select"
                                    ? currentInput.renderPreferences
                                        .options || [{ label: "", value: "" }]
                                    : undefined,
                              };
                            }

                            setFormData({ ...formData, inputs: updatedInputs });
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="text">Short Text</option>
                          <option value="textarea">Long Text</option>
                          <option value="number">Number</option>
                          <option value="select">Dropdown Selection</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm text-gray-600 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={input.description}
                        onChange={(e) =>
                          updateInput(index, "description", e.target.value)
                        }
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        placeholder="Describe what information is needed"
                      />
                    </div>

                    <div className="mt-4">
                      <label className="flex items-center space-x-2 text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={input.required}
                          onChange={(e) =>
                            updateInput(index, "required", e.target.checked)
                          }
                          className="h-4 w-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
                        />
                        <span>This information is required</span>
                      </label>
                    </div>

                    {/* Advanced options - always shown */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Advanced Options
                      </h4>

                      {/* Input Source Options - only for text and textarea */}
                      {(input.type === "text" || input.type === "textarea") && (
                        <div>
                          <label className="block text-sm text-gray-600 mb-2">
                            Allowed Input Sources
                          </label>
                          <div className="flex flex-wrap gap-3 mb-4">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={
                                  input.renderPreferences?.allowedSources?.includes(
                                    "direct"
                                  ) || true
                                }
                                onChange={(e) =>
                                  updateAllowedSources(
                                    index,
                                    "direct",
                                    e.target.checked
                                  )
                                }
                                className="h-4 w-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400 mr-2"
                                disabled // Direct is always enabled
                              />
                              <div className="flex items-center">
                                <FaKeyboard
                                  className="text-gray-500 mr-1"
                                  size={12}
                                />
                                <span className="text-sm text-gray-600">
                                  Direct Input
                                </span>
                              </div>
                            </label>

                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={
                                  input.renderPreferences?.allowedSources?.includes(
                                    "document"
                                  ) || false
                                }
                                onChange={(e) =>
                                  updateAllowedSources(
                                    index,
                                    "document",
                                    e.target.checked
                                  )
                                }
                                className="h-4 w-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400 mr-2"
                              />
                              <div className="flex items-center">
                                <FaFileUpload
                                  className="text-gray-500 mr-1"
                                  size={12}
                                />
                                <span className="text-sm text-gray-600">
                                  Document Upload
                                </span>
                              </div>
                            </label>

                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={
                                  input.renderPreferences?.allowedSources?.includes(
                                    "url"
                                  ) || false
                                }
                                onChange={(e) =>
                                  updateAllowedSources(
                                    index,
                                    "url",
                                    e.target.checked
                                  )
                                }
                                className="h-4 w-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400 mr-2"
                              />
                              <div className="flex items-center">
                                <FaLink
                                  className="text-gray-500 mr-1"
                                  size={12}
                                />
                                <span className="text-sm text-gray-600">
                                  URL Input
                                </span>
                              </div>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Dropdown options - only for select type */}
                      {input.type === "select" && (
                        <div className="mt-3">
                          <label className="block text-sm text-gray-600 mb-2">
                            Dropdown Options
                          </label>
                          <div className="space-y-2">
                            {(input.renderPreferences?.options || []).map(
                              (option, optionIndex) => (
                                <div
                                  key={optionIndex}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    type="text"
                                    value={option.label}
                                    onChange={(e) =>
                                      updateInputOptions(
                                        index,
                                        optionIndex,
                                        "label",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Display text"
                                    className="flex-1 p-2 border border-gray-300 rounded-md text-sm"
                                  />
                                  <input
                                    type="text"
                                    value={option.value}
                                    onChange={(e) =>
                                      updateInputOptions(
                                        index,
                                        optionIndex,
                                        "value",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Value"
                                    className="flex-1 p-2 border border-gray-300 rounded-md text-sm"
                                  />
                                  <button
                                    onClick={() =>
                                      removeOption(index, optionIndex)
                                    }
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    <FaTrash size={14} />
                                  </button>
                                </div>
                              )
                            )}
                            <button
                              onClick={() => addOption(index)}
                              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            >
                              <FaPlus className="inline mr-1" size={10} /> Add
                              Option
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Outputs Tab */}
        {activeTab === "outputs" && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <div>
                <h3 className="text-lg font-medium text-gray-700">
                  Results (What the AI Will Return)
                </h3>
                <p className="text-sm text-gray-500">
                  Define what information the AI will provide in response
                </p>
              </div>
              <button
                onClick={addOutput}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
              >
                <FaPlus className="inline mr-1" /> Add Result
              </button>
            </div>

            <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded mb-4">
              <p className="text-sm text-blue-700">
                Most tools need at least one result. Simple tools typically
                return a single text result.
              </p>
            </div>

            {formData.outputs.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-500">
                  No results defined yet. Add some using the button above.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.outputs.map((output, index) => (
                  <div
                    key={index}
                    className={`p-4 border ${
                      validationState.errors[`output_${index}`]
                        ? "border-red-500 bg-red-50"
                        : "border-gray-200"
                    } rounded-md`}
                  >
                    <div className="flex justify-between mb-4">
                      <span className="text-sm font-medium">
                        Result #{index + 1}
                      </span>
                      {index > 0 && (
                        <button
                          onClick={() => removeOutput(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTrash size={14} />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={output.name}
                          onChange={(e) =>
                            updateOutput(index, "name", e.target.value)
                          }
                          className={`w-full p-2 border ${
                            validationState.errors[`output_${index}`]
                              ? "border-red-500 bg-red-50"
                              : "border-gray-300"
                          } rounded-md text-sm`}
                          placeholder="e.g., summary, answer"
                        />
                        {validationState.errors[`output_${index}`] && (
                          <p className="mt-1 text-sm text-red-600">
                            {validationState.errors[`output_${index}`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Type
                        </label>
                        <select
                          value={output.type}
                          onChange={(e) =>
                            updateOutput(index, "type", e.target.value)
                          }
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="string">Text</option>
                          <option value="array">List</option>
                          <option value="object">Object</option>
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-600 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={output.description}
                          onChange={(e) =>
                            updateOutput(index, "description", e.target.value)
                          }
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          placeholder="Describe what the AI will return"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isCreating || isUpdating}
            className="px-4 py-2 bg-yellow-400 text-gray-800 rounded-md hover:bg-yellow-500 flex items-center"
          >
            {isCreating || isUpdating ? (
              <>
                <HashLoader size={15} color="#4B5563" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <FaSave className="mr-2" />
                Save Tool
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default PromptEditor;
